import { create } from "zustand";
import { persist } from "zustand/middleware";

export type CartExtra = { id: string; name: string; price: number };

export type CartItem = {
  id: string;
  title: string;
  price: number;
  qty: number;
  image?: string;
  extras?: CartExtra[];
  sizeKey?: string | null;
  taste?: CartExtra | null;
};

type CartState = {
  items: CartItem[];
  add: (p: Omit<CartItem, "qty">, qty?: number) => void;
  remove: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  clear: () => void;
  replacePrices: (priceMap: Map<string, number>) => void;
};

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (p, qty = 1) =>
        set(({ items }) => {
          const idx = items.findIndex((i) => i.id === p.id);
          if (idx >= 0) {
            const next = [...items];
            next[idx] = { ...next[idx], qty: next[idx].qty + qty };
            return { items: next };
          }
          return { items: [...items, { ...p, qty }] };
        }),
      remove: (id) =>
        set(({ items }) => ({ items: items.filter((i) => i.id !== id) })),
      setQty: (id, qty) =>
        set(({ items }) => ({
          items:
            qty <= 0
              ? items.filter((i) => i.id !== id)
              : items.map((i) => (i.id === id ? { ...i, qty } : i)),
        })),
      replacePrices: (priceMap) =>
        set((s) => ({
          items: s.items.map((x) =>
            priceMap.has(x.id) ? { ...x, price: priceMap.get(x.id)! } : x
          ),
        })),
      clear: () => set({ items: [] }),
    }),
    { name: "cart:v1" }
  )
);

export const useCartCount = () =>
  useCart((s) => s.items.reduce((n, i) => n + i.qty, 0));
export const useCartTotal = () =>
  useCart((s) => s.items.reduce((sum, i) => sum + i.qty * i.price, 0));
